// client/src/modules/support/routes.tsx
import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSupportPreferences } from '../../hooks/support/useSupportPreferences';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { FormConsumerProvider } from '../../contexts/FormConsumerContext';
import { lazyWithRetry } from '../../lib/lazyWithRetry';

// Lazy load Support pages for code splitting
const SupportDashboard = lazyWithRetry(() => import('../../pages/Support/SupportDashboard'));
const SupportInbox = lazyWithRetry(() => import('../../pages/Support/SupportInbox'));
const SupportOnCall = lazyWithRetry(() => import('../../pages/Support/SupportOnCall'));
const CallLogCreate = lazyWithRetry(() => import('../../pages/Support/CallLogCreate'));
const SupportCaseCreate = lazyWithRetry(() => import('../../pages/Support/SupportCaseCreate'));
const ServiceTicketDetail = lazyWithRetry(() => import('../../pages/ServiceTickets/ServiceTicketDetail').then(m => ({ default: m.ServiceTicketDetail })));
const SupportSettings = lazyWithRetry(() => import('../../pages/Support/SupportSettings'));
const SupportLogins = lazyWithRetry(() => import('../../pages/Support/SupportLogins'));
const LiveCallScreen = lazyWithRetry(() => import('../../pages/Support/LiveCallScreen'));
const EmailSignatureBuilder = lazyWithRetry(() => import('../../pages/Support/EmailSignatureBuilder'));

// Form consumer pages — shared with P8 Forms module
const FormSubmissionList = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionList'));
const FormSubmissionDetail = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionDetail'));

/**
 * Wraps form consumer pages so navigation stays within /support/forms/*
 */
function SupportFormsConsumer({ children }: { children: React.ReactNode }) {
  return (
    <FormConsumerProvider basePath="/support/forms" moduleLabel="Web Forms" moduleId="P5">
      {children}
    </FormConsumerProvider>
  );
}

/**
 * Role-based landing redirect for /support.
 * User preference overrides role default.
 * Managers/admins land on dashboard; all others land on inbox.
 */
function SupportRedirect() {
  const { user } = useAuth();
  const { data: prefs } = useSupportPreferences();

  // User preference overrides role default
  if (prefs?.defaultLandingPage) {
    return <Navigate to={`/support/${prefs.defaultLandingPage}`} replace />;
  }

  // Role-based default: managers/admins → dashboard, others → inbox
  const role = (user?.role || '').toLowerCase();
  const isManager = ['admin', 'manager', 'owner'].includes(role);

  return <Navigate to={isManager ? '/support/dashboard' : '/support/inbox'} replace />;
}

/** Redirect to inbox with ?selected= query param for in-place detail view */
function RedirectToInboxSelected() {
  const params = useParams();
  const id = params.callLogId ?? params.caseId ?? params.id;
  return <Navigate to={`/support/inbox?selected=${id}`} replace />;
}

/**
 * Support module routes - relative to /support prefix.
 * Index redirects to dashboard (default landing page).
 * Incoming call banner is handled in App.tsx via useIncomingCall,
 * scoped by user's callNotificationMode preference and current route.
 *
 * List/detail routes (calls, cases, activity) are replaced with redirects
 * to /support/inbox?selected=:id for unified select-in-place UX.
 */
export const routes: RouteObject[] = [
  // Default landing — role-based redirect (managers → dashboard, others → inbox)
  {
    index: true,
    element: <ProtectedRoute><SupportRedirect /></ProtectedRoute>,
  },

  // Dashboard (management overview)
  {
    path: 'dashboard',
    element: <ProtectedRoute><SupportDashboard /></ProtectedRoute>,
  },

  // Inbox (operations board)
  {
    path: 'inbox',
    element: <ProtectedRoute><SupportInbox /></ProtectedRoute>,
  },

  // Inbox creation + live pages (must be ABOVE :id redirects)
  {
    path: 'inbox/calls/new',
    element: <ProtectedRoute><CallLogCreate /></ProtectedRoute>,
  },
  {
    path: 'inbox/calls/live',
    element: <ProtectedRoute><LiveCallScreen /></ProtectedRoute>,
  },
  {
    path: 'inbox/cases/new',
    element: <ProtectedRoute><SupportCaseCreate /></ProtectedRoute>,
  },

  // Inbox list/detail routes → redirect to unified inbox with selection
  {
    path: 'inbox/calls',
    element: <Navigate to="/support/inbox" replace />,
  },
  {
    path: 'inbox/calls/:callLogId',
    element: <RedirectToInboxSelected />,
  },
  {
    path: 'inbox/cases',
    element: <Navigate to="/support/inbox" replace />,
  },
  {
    path: 'inbox/cases/:caseId',
    element: <RedirectToInboxSelected />,
  },
  {
    path: 'inbox/activity',
    element: <Navigate to="/support/inbox" replace />,
  },

  // On-call rota — who takes after-hours calls, by branch and week. Any
  // employee of the organization can READ it (firestore.rules: isEmployee &&
  // belongsToOrg — no P5 check). Writes need Support access: the page renders
  // read-only without it, and the support_dispatch callable enforces the
  // office gate on every write.
  {
    path: 'on-call',
    element: <ProtectedRoute><SupportOnCall /></ProtectedRoute>,
  },

  // Logins (shared credential vault)
  {
    path: 'logins',
    element: <ProtectedRoute><SupportLogins /></ProtectedRoute>,
  },

  // Email signature builder.
  // ProtectedRoute only (no module gate) on purpose: everyone at OnPoint needs a
  // signature, but Support module access is per-user, so people without P5 reach
  // this by direct link or from their Profile page.
  {
    path: 'signature',
    element: <ProtectedRoute><EmailSignatureBuilder /></ProtectedRoute>,
  },

  // Settings (unified)
  {
    path: 'settings',
    element: <ProtectedRoute><SupportSettings /></ProtectedRoute>,
  },

  // Service tickets (unchanged)
  {
    path: 'tickets/:ticketId',
    element: <ProtectedRoute><ServiceTicketDetail /></ProtectedRoute>,
  },

  // ============================================================================
  // BACKWARDS COMPATIBILITY REDIRECTS
  // Old routes → inbox with ?selected= (no double-redirect chains)
  // ============================================================================
  {
    path: 'calls',
    element: <Navigate to="/support/inbox" replace />,
  },
  {
    path: 'calls/new',
    element: <Navigate to="/support/inbox/calls/new" replace />,
  },
  {
    path: 'calls/live',
    element: <Navigate to="/support/inbox/calls/live" replace />,
  },
  {
    path: 'calls/:callLogId',
    element: <RedirectToInboxSelected />,
  },
  {
    path: 'cases',
    element: <Navigate to="/support/inbox" replace />,
  },
  {
    path: 'cases/new',
    element: <Navigate to="/support/inbox/cases/new" replace />,
  },
  {
    path: 'cases/:caseId',
    element: <RedirectToInboxSelected />,
  },
  {
    path: 'activity',
    element: <Navigate to="/support/inbox" replace />,
  },
  {
    path: 'settings/daily-report',
    element: <Navigate to="/support/settings" replace />,
  },

  // ---------------------------------------------------------------------------
  // Form consumer routes — web form submissions, no P8 access required
  // ---------------------------------------------------------------------------
  {
    path: 'forms/submissions',
    element: <ProtectedRoute><SupportFormsConsumer><FormSubmissionList /></SupportFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions/:submissionId',
    element: <ProtectedRoute><SupportFormsConsumer><FormSubmissionDetail /></SupportFormsConsumer></ProtectedRoute>,
  },

  // Catch-all
  {
    path: '*',
    element: <Navigate to="/support/dashboard" replace />,
  },
];
